import React, { useCallback, useContext, useMemo } from 'react'
import PropertyPriceMarker from './PropertyPriceMarker'
import { format as formatCurrency } from 'lib/format/formatCurrencyIntl'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import PillMarker from './PillMarker'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
} from 'constants/offer'
import config from 'constants/config'
import { OfferBestPriceData } from 'selectors/offerSelectors'
import {
  useOptimizelyExperiment,
  OptimizelyClientSideExperiments,
} from 'lib/optimizely/optimizelyUtils'
import { getLowestPricePackage } from 'lib/offer/getLowestPricePackage'
import { OverlayView, OverlayViewF } from '@react-google-maps/api'
import PropertyMarker from 'components/Search/SearchResultsMap/PropertyMarker'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import Clickable from 'components/Common/Clickable'
import SearchResultsMapOfferView from 'components/Search/SearchResultsMap/SearchResultsMapOfferView'
import { calculateCellPositionAndDimensions } from 'lib/maps/googleMapUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { calculateDiscount } from 'checkout/selectors/payment/generic'

const MAX_PIN_LIMIT = config.search.mapView.pinLimit

const StyledContainer = styled.div<{
  buttonWidth: number;
  buttonHeight: number;
}>`
  position: absolute;
  z-index: 5;
  padding: ${rem(8)};
  &.col0-row0 {
    transform: translate(0, 0);
  }
  &.col1-row0 {
    transform: translate(-50%, 0%);
  }
  &.col2-row0 {
    transform: translate(calc(-100% + ${(props) => props.buttonWidth}px), 0%);
  }
  &.col0-row1 {
    transform: translate(calc(0% + ${(props) => props.buttonWidth}px), -50%);
  }
  &.col1-row1 {
    transform: translate(calc(0% + ${(props) => props.buttonWidth}px), -50%);
  }
  &.col2-row1 {
    transform: translate(-100%, -50%);
  }
  &.col0-row2 {
    transform: translate(
      0%,
      calc(-100% - ${(props) => props.buttonHeight}px)
    );
  }
  &.col1-row2 {
    transform: translate(
      -50%,
      calc(-100% - ${(props) => props.buttonHeight}px)
    );
  }
  &.col2-row2 {
    transform: translate(
      -80%,
      calc(-100% - ${(props) => props.buttonHeight}px)
    );
  }
`

interface Props {
  onMarkerClick: (
    marker: App.SearchMapMarker,
    pinType: string,
    e?: Event | React.MouseEvent<Element | MouseEvent>
  ) => void;
  bestPrices: Record<string, OfferBestPriceData>;
  isActive: boolean;
  isVisited: boolean;
  filters: App.OfferListFilters;
  onMarkerLoad?: (
    markerInstance: google.maps.Marker,
    markerData: App.SearchMapMarker
  ) => void;
  onMarkerUnmount?: (markerInstance: google.maps.Marker) => void;
  onDeselectOffers?: () => void;
  marker: App.SearchMapMarker;
  index: number;
  handleProductClick?: (
    offer:
      | App.Offer
      | App.OfferSummary
      | App.BedbankOffer
      | App.BedbankOfferSummary,
    position: number
  ) => void;
}

function MapMarker(props: Props) {
  const {
    onMarkerLoad,
    onMarkerUnmount,
    marker,
    index,
    onMarkerClick,
    isActive,
    bestPrices,
    isVisited,
    filters,
    onDeselectOffers,
    handleProductClick,
  } = props

  const [cellPosition, setCellPosition] = React.useState<{
    row: number;
    column: number;
  } | null>(null)
  const [elementDimensions, setElementDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null)
  const geo = useContext(GeoContext)
  const currencyCode = useContext(CurrencyContext)

  const hasDates = !!filters?.checkIn

  const shouldShowMapPinPrices = !!useOptimizelyExperiment(
    OptimizelyClientSideExperiments.searchNightlyPriceMapMarkers,
  )

  const calculateZIndex = useCallback(
    (marker: App.SearchMapMarker, index: number) => {
      if (isActive) {
        return MAX_PIN_LIMIT + 1
      }
      if (index >= MAX_PIN_LIMIT) {
        return 0
      }
      const offerType = marker.offers[0].type
      if (
        offerType === OFFER_TYPE_HOTEL ||
        offerType === OFFER_TYPE_ALWAYS_ON ||
        offerType === OFFER_TYPE_BED_BANK
      ) {
        return MAX_PIN_LIMIT - index
      }
      return 0
    },
    [isActive],
  )

  const { label, discount } = useMemo(() => {
    const offer = marker.offers[0]
    const { loading, bestPrice } = bestPrices[offer.id]

    if (loading) {
      return {}
    }

    const bedbankSellPrice = 'sell' in offer ? offer.sell?.price : undefined
    const bedbankDiscount = 'sell' in offer ? offer.sell?.discount : undefined
    const minimumDuration = 'minDuration' in offer ? offer.minDuration : undefined

    const lowestPricePackage = offer.type !== OFFER_TYPE_BED_BANK ? getLowestPricePackage(offer, offer.packages, true) : undefined
    const lowestPrice = lowestPricePackage?.price ?? bedbankSellPrice
    const discount = lowestPricePackage ? calculateDiscount(lowestPricePackage.price, lowestPricePackage.value) : bedbankDiscount

    const totalPrice =
        (hasDates ? bestPrice : lowestPrice) ?? 0

    const displayedPrice =
        hasDates ?
          bestPrice :
          totalPrice / (minimumDuration ?? 1)

    const formattedPrice = formatCurrency(
      displayedPrice ?? 0,
      currencyCode,
      geo.currentRegionCode,
      'roundedDollar',
    )

    return {
      label: displayedPrice && formattedPrice ? formattedPrice : undefined,
      discount,
    }
  }, [bestPrices, currencyCode, geo.currentRegionCode, hasDates, marker.offers])

  const isMobile = useIsMobileScreen()

  const handleClick = useCallback(
    (e: Event | React.MouseEvent<Element | MouseEvent>) => {
      onMarkerClick(marker, index < MAX_PIN_LIMIT ? 'pricePin' : 'pill', e)
      const { row, column, width, height } =
        calculateCellPositionAndDimensions(e)
      setCellPosition({ row, column })
      setElementDimensions({ width, height })
      e.stopPropagation()
    },
    [index, marker, onMarkerClick],
  )

  return (
    <OverlayViewF
      key={marker.id}
      position={{
        lat: marker.latitude ?? 0,
        lng: marker.longitude ?? 0,
      }}
      zIndex={calculateZIndex(marker, index)}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <Clickable onClick={handleClick}>
        {!shouldShowMapPinPrices && (
          <PropertyMarker
            key={marker.id}
            marker={marker}
            active={isActive}
            visited={isVisited}
            onUnmount={onMarkerUnmount}
            onLoad={onMarkerLoad}
            onClick={(e: google.maps.MapMouseEvent) => handleClick(e.domEvent)}
            scaleDown={isMobile}
            soldOut={marker.offers.every(
              (offer) => bestPrices[offer.id]?.soldOut,
            )}
          />
        )}
        {shouldShowMapPinPrices &&
          (index < MAX_PIN_LIMIT || isActive) &&
          !bestPrices[marker.offers[0].id].loading && (
            <PropertyPriceMarker
              isVisited={isVisited}
              isActive={isActive}
              label={label}
              discount={discount}
            />
        )}
        {shouldShowMapPinPrices &&
          index >= MAX_PIN_LIMIT &&
          !isActive &&
          !bestPrices[marker.offers[0].id].loading && (
            <PillMarker
              isVisited={isVisited}
              isActive={isActive}
              marker={marker}
            />
        )}
      </Clickable>
      <CSSBreakpoint min="desktop">
        {isActive && (
          <StyledContainer
            className={`col${cellPosition?.column}-row${cellPosition?.row}`}
            buttonWidth={elementDimensions?.width ?? 0}
            buttonHeight={elementDimensions?.height ?? 0}
          >
            <SearchResultsMapOfferView
              key={marker.id}
              leOfferIds={marker.offers.map((offer) => offer.id)}
              filters={filters}
              onProductClick={handleProductClick}
              onDeselectOffers={onDeselectOffers}
            />
          </StyledContainer>
        )}
      </CSSBreakpoint>
    </OverlayViewF>
  )
}

export default MapMarker
